import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Define the database types based on our schema
export interface Database {
  public: {
    Tables: {
      patients: {
        Row: {
          id: string;
          patient_id: string;
          first_name: string;
          last_name: string;
          date_of_birth: string | null;
          email: string | null;
          phone: string | null;
          address: string | null;
          emergency_contact_name: string | null;
          emergency_contact_phone: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          patient_id: string;
          first_name: string;
          last_name: string;
          date_of_birth?: string | null;
          email?: string | null;
          phone?: string | null;
          address?: string | null;
          emergency_contact_name?: string | null;
          emergency_contact_phone?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          patient_id?: string;
          first_name?: string;
          last_name?: string;
          date_of_birth?: string | null;
          email?: string | null;
          phone?: string | null;
          address?: string | null;
          emergency_contact_name?: string | null;
          emergency_contact_phone?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      appointments: {
        Row: {
          id: string;
          patient_id: string;
          appointment_date: string;
          appointment_time: string | null;
          procedure_type: string | null;
          doctor_name: string | null;
          location: string | null;
          status: string;
          notes: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          patient_id: string;
          appointment_date: string;
          appointment_time?: string | null;
          procedure_type?: string | null;
          doctor_name?: string | null;
          location?: string | null;
          status?: string;
          notes?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          patient_id?: string;
          appointment_date?: string;
          appointment_time?: string | null;
          procedure_type?: string | null;
          doctor_name?: string | null;
          location?: string | null;
          status?: string;
          notes?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      form_templates: {
        Row: {
          id: string;
          name: string;
          description: string | null;
          version: number;
          config: any;
          is_active: boolean;
          created_by: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          description?: string | null;
          version?: number;
          config: any;
          is_active?: boolean;
          created_by?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          description?: string | null;
          version?: number;
          config?: any;
          is_active?: boolean;
          created_by?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      questions: {
        Row: {
          id: string;
          category: string;
          question_text: string;
          question_type: string;
          options: any | null;
          required: boolean;
          conditional_logic: any | null;
          order_index: number;
          is_active: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          category: string;
          question_text: string;
          question_type: string;
          options?: any | null;
          required?: boolean;
          conditional_logic?: any | null;
          order_index?: number;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          category?: string;
          question_text?: string;
          question_type?: string;
          options?: any | null;
          required?: boolean;
          conditional_logic?: any | null;
          order_index?: number;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      devices: {
        Row: {
          id: string;
          device_id: string;
          device_name: string | null;
          mdm_group: string | null;
          location: string | null;
          is_active: boolean;
          last_seen: string;
          app_version: string | null;
          os_version: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          device_id: string;
          device_name?: string | null;
          mdm_group?: string | null;
          location?: string | null;
          is_active?: boolean;
          last_seen?: string;
          app_version?: string | null;
          os_version?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          device_id?: string;
          device_name?: string | null;
          mdm_group?: string | null;
          location?: string | null;
          is_active?: boolean;
          last_seen?: string;
          app_version?: string | null;
          os_version?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      consent_forms: {
        Row: {
          id: string;
          patient_id: string;
          appointment_id: string;
          template_id: string | null;
          device_id: string | null;
          form_data: any;
          signature_data: string | null;
          status: string;
          sharepoint_url: string | null;
          pdf_file_path: string | null;
          completed_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          patient_id: string;
          appointment_id: string;
          template_id?: string | null;
          device_id?: string | null;
          form_data: any;
          signature_data?: string | null;
          status?: string;
          sharepoint_url?: string | null;
          pdf_file_path?: string | null;
          completed_at?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          patient_id?: string;
          appointment_id?: string;
          template_id?: string | null;
          device_id?: string | null;
          form_data?: any;
          signature_data?: string | null;
          status?: string;
          sharepoint_url?: string | null;
          pdf_file_path?: string | null;
          completed_at?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      storage_config: {
        Row: {
          id: string;
          site_url: string;
          document_library: string;
          folder_path: string | null;
          client_id: string;
          client_secret: string | null;
          tenant_id: string | null;
          access_token: string | null;
          refresh_token: string | null;
          is_configured: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          site_url: string;
          document_library: string;
          folder_path?: string | null;
          client_id: string;
          client_secret?: string | null;
          tenant_id?: string | null;
          access_token?: string | null;
          refresh_token?: string | null;
          is_configured?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          site_url?: string;
          document_library?: string;
          folder_path?: string | null;
          client_id?: string;
          client_secret?: string | null;
          tenant_id?: string | null;
          access_token?: string | null;
          refresh_token?: string | null;
          is_configured?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      form_responses: {
        Row: {
          id: string;
          patient_name: string;
          form_template_id: string;
          form_template_name: string;
          responses: any;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          patient_name: string;
          form_template_id: string;
          form_template_name: string;
          responses: any;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          patient_name?: string;
          form_template_id?: string;
          form_template_name?: string;
          responses?: any;
          created_at?: string;
          updated_at?: string;
        };
      };
    };
  };
}

interface SupabaseContextType {
  supabase: SupabaseClient<Database>;
  isConnected: boolean;
}

const SupabaseContext = createContext<SupabaseContextType | undefined>(undefined);

export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (context === undefined) {
    throw new Error('useSupabase must be used within a SupabaseProvider');
  }
  return context;
};

interface SupabaseProviderProps {
  children: React.ReactNode;
}

export const SupabaseProvider: React.FC<SupabaseProviderProps> = ({ children }) => {
  const [supabase] = useState(() => {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
    
    return createClient<Database>(supabaseUrl, supabaseKey, {
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
    });
  });

  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Test connection by checking if we can reach Supabase
    const testConnection = async () => {
      try {
        // Simple ping to test if Supabase is reachable
        const { data, error } = await supabase.from('form_templates').select('count').limit(0);
        // If we get here, the connection is working (even if no data)
        setIsConnected(true);
      } catch (err) {
        console.error('Failed to connect to Supabase:', err);
        setIsConnected(false);
      }
    };

    testConnection();

    // Set up real-time subscription for form templates
    const subscription = supabase
      .channel('form_templates_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'form_templates' },
        (payload) => {
          console.log('Form template change:', payload);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase]);

  return (
    <SupabaseContext.Provider value={{ supabase, isConnected }}>
      {children}
    </SupabaseContext.Provider>
  );
};
